import React from "react";
import GeotabDayPicker from "./GeotabDayPicker.jsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchGeotabAssignment, fetchGeotabComparison, fetchGeotabTraces } from "../../api/geotabApi.js";
import DispatchGanttPreview from "../consolidation/DispatchGanttPreview.jsx";
import { HoverCard } from "../ResultsPrimitives.jsx";
import GeotabBusy from "./GeotabBusy.jsx";
import GeotabBadge, { DriverCard } from "./GeotabBadge.jsx";
import GeotabGantt from "./GeotabGantt.jsx";
import MapLink from "./GeotabMapLink.jsx";
import { Hint, HoverRows } from "./GeotabHover.jsx";
import GeotabReleve from "./GeotabReleve.jsx";
import useAccess from "../../hooks/useAccess.jsx";
import GeotabDriverDay from "./GeotabDriverDay.jsx";
import GeotabFleetSummary from "./GeotabFleetSummary.jsx";
import { DayFindings, FindingsPanel } from "./GeotabFindings.jsx";
import { FLEET, fleetItems, truckItems } from "./geotabFindings.js";
import GeotabMap from "./GeotabMap.jsx";
import { LINK_CLASS as LINK } from "./geotabLinks.js";
import { getLocalTodayDispatchDate } from "../../utils/dispatchDayCalendarUtils.js";
import GeotabPlanBar from "./GeotabPlanBar.jsx";
import { THRESHOLD_CHOICES } from "./geotabPreferences.js";
import {
  DELIVERY_FILL, LOADING_FILL, OFF_COLOR, STATUS_TEXT, deltaTone, driveLegs, driverOf, fmtDelta, fmtDistance, fmtDuration, fmtTime, isOtherDriver, meanDurations, stepLabel, traceSegments, truckLabel,
} from "./geotabFormat.js";

// Journée en cours : relecture des trajets toutes les 2 minutes, onglet visible seulement (calcul léger côté serveur).
const REFRESH_MS = 120000;
// 110 px : « Richard Brouillard » en entier, un nom plus long est tronqué.
const LIST_COLS = "grid grid-cols-[110px_34px_46px_36px_48px_34px_42px] items-center gap-1";
const SELECT = "h-6 rounded-[4px] border border-slate-300 bg-white py-0 pl-1.5 pr-6 text-[12px] font-semibold text-slate-800";
const LINK_PRESSED = "inline-flex h-7 items-center rounded-[4px] border border-accent bg-accent px-2.5 text-[12.5px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong";

const PLAN_COMPARISON_HELP = (
  <div className="space-y-2 text-slate-700">
    <p className="m-0"><b>Plan de clôture :</b> le Dispatch enregistré à la clôture, réaffectations manuelles comprises. Pour une journée ouverte, c’est le Dispatch en cours avec les dernières modifications enregistrées.</p>
    <p className="m-0"><b>Réglages actuels :</b> la même tournée, dans le même ordre, recalculée avec les paramètres déjà enregistrés aujourd’hui. Les suggestions du Calibrage sont utilisées seulement après avoir cliqué sur <b>Appliquer</b>.</p>
    <div className="space-y-1 rounded-[4px] border border-slate-200 bg-slate-50 p-2">
      <p className="m-0 font-bold text-slate-900">Exemple : un calibrage modifié ce matin</p>
      <p className="m-0">Hier, le camion était réglé à <b className="whitespace-nowrap">40 t/h</b>. Décharger <b className="whitespace-nowrap">20 t</b> représentait <b className="whitespace-nowrap">30 min</b>.</p>
      <p className="m-0">Ce matin, dans <b>Calibrage → À ajuster</b>, vous appliquez une suggestion : le débit passe à <b className="whitespace-nowrap">30 t/h</b>.</p>
      <p className="m-0">En consultant cette livraison d’hier :</p>
      <p className="m-0"><b>Plan de clôture :</b> <b className="whitespace-nowrap">30 min</b>, avec le débit d’hier.</p>
      <p className="m-0"><b>Réglages actuels :</b> <b className="whitespace-nowrap">40 min</b>, avec le débit modifié ce matin.</p>
    </div>
    <p className="m-0">Pour comparer le Dispatch d’aujourd’hui au réel en cours, ou celui d’hier au réel, choisissez <b>Plan de clôture</b>.</p>
    <p className="m-0">Sans changement de paramètres, les deux modes devraient donner les mêmes prévisions. Le réel Geotab reste identique.</p>
  </div>
);

// « 8 oct. à 20:27 » dans le fuseau du navigateur.
const fmtStamp = (iso) => new Date(iso).toLocaleString("fr-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).replace(",", " à");

function shiftDate(value, days) {
  const d = new Date(`${value}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// Pourquoi l'écart du temps travaillé : temps sur place, arrêts hors plan et route, écrits comme une addition. Sans le départ :
// un chauffeur peut partir à 5 h ou à 7 h.
function WhyLine({ cmp, worked, tolerance }) {
  const lunch = cmp.sites.reduce((sum, s) => sum + (s.lunch_after || 0), 0);
  const stay = cmp.sites.reduce((sum, s) => sum + (s.real_dwell ?? 0) - s.plan_dwell, 0);
  // Arrêts hors plan, la pause dîner prévue en moins : le réel la compte comme un arrêt.
  const off = cmp.visits.filter((v) => v.kind !== "site" && !v.after_day && v.arr < cmp.end_at).reduce((sum, v) => sum + v.dwell, 0) - lunch;
  // Arrondis avant la somme : la route prend le reste, la ligne tombe juste à la minute.
  const [stayMin, offMin, total] = [stay, off, worked].map(Math.round);
  const parts = [["Sur place", stayMin], [lunch ? "Hors plan et dîner" : "Hors plan", offMin], ["Route", total - stayMin - offMin], ["Temps travaillé", total]];
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-slate-200 px-1 pt-1.5 text-[12.5px] font-semibold text-slate-800">
      <b className="mr-1 text-slate-900">Pourquoi le temps travaillé à {fmtDelta(total)} :</b>
      {parts.map(([label, value], i) => (
        <React.Fragment key={label}>
          {i > 0 && <span className="text-[14px] font-extrabold text-slate-900">{i === parts.length - 1 ? "=" : "+"}</span>}
          <span className="inline-flex items-center gap-1">{label}<span className="inline-block w-16"><GeotabBadge tone={deltaTone(value, tolerance)}>{fmtDelta(value)}</GeotabBadge></span></span>
        </React.Fragment>
      ))}
    </div>
  );
}

// Temps travaillé, puis route, chargement et livraison en moyenne : Prévu · Réel · Écart, comme le tableau « Durées par étape ».
// Une moyenne dit combien d'étapes elle compte (« 3 sur 6 »).
// Sans le style global des tableaux (fond, en-tête collant, grand espacement des lettres).
const STAT_CELL = "static border-b-0 bg-transparent px-1.5 py-0 whitespace-nowrap";
function StatTable({ stats }) {
  const sep = "border-l border-slate-300";
  return (
    <table className="w-auto min-w-0 tabular-nums">
      <thead>
        <tr>{stats.map(([label, , , , count], i) => <th key={label} colSpan={3} className={`${STAT_CELL} text-[11px] font-extrabold tracking-wide text-slate-900 ${i ? sep : ""}`}>
          {label}{count && <span className="ml-1 text-[10.5px] font-semibold text-slate-500">{count}</span>}</th>)}</tr>
        <tr>{stats.map(([label, , , , count], i) => [count ? "Prévu moy." : "Prévu", count ? "Réel moy." : "Réel", "Écart"].map((t, j) => <th key={`${label}${t}`} className={`${STAT_CELL} pb-0.5 text-[10.5px] font-bold tracking-normal text-slate-600 ${i && !j ? sep : ""}`}>{t}</th>))}</tr>
      </thead>
      <tbody>
        <tr className="text-[14px] hover:bg-transparent">
          {stats.map(([label, planned, real, delta], i) => (
            <React.Fragment key={label}>
              <td className={`${STAT_CELL} align-middle text-slate-700 ${i ? sep : ""}`}>{planned}</td>
              <td className={`${STAT_CELL} align-middle font-bold text-slate-900`}>{real}</td>
              <td className={`${STAT_CELL} align-middle`}><span className="inline-block w-16">{delta}</span></td>
            </React.Fragment>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function TruckList({ rows, selected, onSelect, tolerance }) {
  // Sur téléphone, la liste défile dans son cadre ; son en-tête n'y reste plus collé en haut.
  return (
    <div className="max-w-full max-[720px]:overflow-x-auto">
    <div className="w-max min-w-0">
      <div className={`${LIST_COLS} sticky top-0 z-10 border border-transparent bg-slate-50 px-2 pb-1 text-center text-[10px] font-bold leading-tight text-slate-500`}>
        <span className="text-left">Camionneur</span><span>Sites</span><span>Ordre</span><span>Chauff.</span><span>Sur place</span><span>Hors plan</span><span>Adresse</span>
      </div>
      <div className="overflow-hidden rounded-[4px] border border-slate-200 bg-white">
        <button type="button" onClick={() => onSelect(FLEET)} aria-pressed={selected === FLEET}
          className={`block w-full border-b border-slate-200 px-2 py-1 text-left hover:bg-slate-50 ${selected === FLEET ? "bg-slate-100 shadow-[inset_3px_0_0_var(--color-accent)]" : ""}`}>
          <b className="block text-[12.5px] leading-tight text-slate-900">Toute la flotte</b>
          <span className="block text-[11px] leading-tight text-slate-500">{rows.filter((r) => r.comparison).length} camions comparés · résumé et constats</span>
        </button>
        {rows.map((row) => {
          const cmp = row.comparison;
          const sites = cmp?.sites.filter((s) => s.kind !== "depot") || [];
          const found = sites.filter((s) => s.real_arrival != null).length;
          const missing = sites.length - found;
          const toCheck = sites.filter((s) => s.match === "address" || s.match === "place").length;
          // Écart moyen sur place, chargements et livraisons mesurés.
          const stay = meanDurations(sites.map((s) => [s.plan_dwell, s.real_dwell]));
          const other = isOtherDriver(row);
          const active = row.truck_id === selected;
          return (
            <Hint key={row.truck_id} text={STATUS_TEXT[row.status] || (other ? `Session Geotab au nom de ${row.driver_geotab} : vérifiez qui a conduit dans le relevé.` : "")} placement="right">
            <button type="button" onClick={() => onSelect(row.truck_id)} aria-pressed={active}
              className={`block w-full border-t border-slate-100 px-2 py-1 text-left first:border-t-0 hover:bg-slate-50 ${active ? "bg-slate-100 shadow-[inset_3px_0_0_var(--color-accent)]" : ""}`}>
              <div className={LIST_COLS}>
                <div className="min-w-0">
                  <b className="block truncate text-[12.5px] leading-tight text-slate-900">{driverOf(row)}</b>
                  <span className="block truncate text-[11px] leading-tight text-slate-500">{truckLabel(row)}{other ? ` · Geotab : ${row.driver_geotab}` : ""}</span>
                </div>
                {cmp ? (
                  <>
                    <GeotabBadge tone={missing ? "bad" : "ok"} title={missing ? `${missing} site(s) non détecté(s)` : "Tous les sites retrouvés"}>{found}/{sites.length}</GeotabBadge>
                    <GeotabBadge tone={cmp.order_ok ? "ok" : "warn"} title={cmp.order_ok ? "Ordre respecté" : "Ordre modifié"}>{cmp.order_ok ? "✓" : "Modifié"}</GeotabBadge>
                    <GeotabBadge tone={other ? "warn" : "ok"} title={other ? `Chauffeur à confirmer · prévu : ${row.driver_plan}` : "Chauffeur prévu"}>{other ? <DriverCard /> : "✓"}</GeotabBadge>
                    {stay ? <GeotabBadge tone={deltaTone(stay.gap, tolerance)} title={`Sur place, en moyenne : ${fmtDuration(stay.plan)} prévues → ${fmtDuration(stay.real)} (${stay.count} arrêts)`}>{fmtDelta(stay.gap)}</GeotabBadge>
                      : <GeotabBadge tone="empty" />}
                    <GeotabBadge tone={cmp.off_stops.length ? "off" : "empty"} title="Arrêts hors plan">{cmp.off_stops.length}</GeotabBadge>
                    <GeotabBadge tone={toCheck ? "warn" : "empty"} title="Adresses à vérifier">{toCheck}</GeotabBadge>
                  </>
                ) : Array.from({ length: 6 }, (_, i) => <GeotabBadge key={i} tone="empty" />)}
              </div>
            </button>
            </Hint>
          );
        })}
      </div>
    </div>
    </div>
  );
}

// Durées par étape : la route pour se rendre à chaque site et le temps sur place, prévus et réels, sans les heures
// (un chauffeur peut partir à 5 h ou à 7 h). Arrêts hors plan et dîner prévu à leur place, totaux au bas.
function SiteTable({ row, tolerance }) {
  const cmp = row.comparison;
  const trucks = row.trucks_used || [];
  const legs = driveLegs(row);
  // Seulement une journée partagée entre plusieurs camions : sinon l'en-tête suffit.
  const otherTruck = (deviceId) => (trucks.length > 1 ? trucks.find((u) => u.device_id === deviceId && !u.planned)?.name : null);
  const cell = "border-t border-slate-100 px-1.5 py-0.5 text-[12.5px] leading-tight whitespace-nowrap";
  const sep = "border-l border-slate-100";
  const gap = (plan, real) => <span className="inline-block w-16"><GeotabBadge tone={deltaTone(real - plan, tolerance)}>{fmtDelta(real - plan)}</GeotabBadge></span>;
  const cells = (plan, real, delta, extra = "") => <><td className={`${cell} ${sep} text-slate-500 ${extra}`}>{plan}</td><td className={`${cell} font-semibold ${extra}`}>{real}</td><td className={`${cell} ${extra}`}>{delta}</td></>;
  const route = (leg) => cells(fmtDuration(leg.plan), leg.real == null ? "—" : fmtDuration(leg.real), leg.real == null ? <span className="text-[11px] font-semibold text-slate-500">{leg.why}</span> : gap(leg.plan, leg.real));
  // Couleurs du Gantt du Dispatch.
  const type = (color, label) => <td className={cell}><span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[2px] align-[-1px] shadow-[inset_0_0_0_1px_rgba(0,0,0,.55)]" style={{ background: color }} />{label}</td>;
  const KIND = { loading: [LOADING_FILL, "Chargement"], delivery: [DELIVERY_FILL, "Livraison"], depot: ["#cbd5e1", "Dépôt"] };
  // Arrêts hors plan juste avant le premier site atteint après eux.
  const off = [...cmp.off_stops].sort((a, b) => a.arr - b.arr);
  const offBefore = (arrival) => (arrival == null ? [] : off.splice(0, off.filter((v) => v.arr < arrival).length));
  const offRow = (v) => (
    <tr key={`off${v.arr}`} className="shadow-[inset_3px_0_0_#c2410c]">
      <td className={`${cell} font-bold text-[#c2410c]`}>!</td><td className={cell} />
      <td className={cell}><Hint text={v.place?.name || `${v.lat.toFixed(4)}, ${v.lon.toFixed(4)}`}><b className="text-[#9a3412]">{v.switch ? "Changement de camion" : v.kind === "depot" ? "Passage au dépôt" : "Arrêt hors plan"}</b></Hint>
        <span className="ml-1 text-[11px] text-slate-500">{fmtTime(v.arr)}–{fmtTime(v.dep)}</span>
        <span className="ml-1 inline-flex align-middle"><MapLink lat={v.lat} lon={v.lon} color={OFF_COLOR} title={`Voir l’arrêt hors plan de ${fmtTime(v.arr)} dans Google Maps`} /></span></td>
      {type(OFF_COLOR, "Hors plan")}{cells()}{cells("—", fmtDuration(v.dwell))}
      <td className={`${cell} ${sep}`}><span className="inline-block w-28">{v.switch ? <GeotabBadge tone="mute">Changement</GeotabBadge> : <GeotabBadge tone="off">Non prévu</GeotabBadge>}</span></td>
    </tr>
  );
  // Totaux comparables : seulement les étapes mesurées des deux côtés (« 2 sur 3 »).
  const stays = (kind) => cmp.sites.filter((s) => s.kind === kind).map((s) => [s.plan_dwell, s.real_dwell]);
  const totals = [["Total chargements", false, stays("loading")], ["Total route", true, legs.map((l) => [l.plan, l.real])], ["Total livraisons", false, stays("delivery")]]
    .filter(([, , pairs]) => pairs.length);
  return (
    <table className="w-full">
      <thead className="text-left text-[10px] font-bold uppercase leading-tight tracking-wide text-slate-500">
        <tr><th className="px-1.5">#</th><Hint text="Numéros d'étape du Gantt du Dispatch"><th className="px-1.5">Gantt</th></Hint><th className="px-1.5">Site</th><th className="px-1.5">Type</th><th colSpan={3} className={`px-1.5 ${sep}`}>Route pour s'y rendre</th><th colSpan={3} className={`px-1.5 ${sep}`}>Sur place</th><th className={`px-1.5 ${sep}`}>Rapprochement</th></tr>
        <tr className="text-[10px] text-slate-400"><th /><th /><th /><th />{["r", "s"].map((k) => ["Prévu", "Réel", "Écart"].map((t, i) => <th key={k + t} className={`px-1.5 ${i === 0 ? sep : ""}`}>{t}</th>))}<th className={sep} /></tr>
      </thead>
      <tbody className="tabular-nums">
        {cmp.sites.map((s, i) => {
          const found = s.real_arrival != null;
          const match = {
            ok: <GeotabBadge tone="ok">Trouvé</GeotabBadge>,
            address: <GeotabBadge tone="warn">{`Adresse ${fmtDistance(s.offset_m)}`}</GeotabBadge>,
            place: <GeotabBadge tone="warn">{`Lieu différent ${fmtDistance(s.offset_m)}`}</GeotabBadge>,
            missing: <GeotabBadge tone="bad">Non détecté</GeotabBadge>,
          }[s.match];
          return (
            <React.Fragment key={s.idx}>
            {offBefore(s.real_arrival).map(offRow)}
            <tr>
              <td className={`${cell} font-bold text-slate-500`}>{s.idx}</td>
              <td className={`${cell} whitespace-nowrap font-semibold text-slate-700`}>{stepLabel(s.steps)}</td>
              <td className={`${cell} max-w-[180px] truncate`}><Hint text={`${s.name}${s.kind !== "depot" ? ` · ${Math.round(s.tons * 10) / 10} t · ${s.order_count} cmd` : ""} · prévu ${fmtTime(s.plan_arrival)}–${fmtTime(s.plan_departure)}${found ? ` · réel ${fmtTime(s.real_arrival)}–${fmtTime(s.real_departure)}` : ""}`}><b className="text-slate-800">{s.name}</b></Hint></td>
              {type(...KIND[s.kind])}
              {route(legs[i])}
              {cells(fmtDuration(s.plan_dwell), s.real_dwell != null ? fmtDuration(s.real_dwell) : found ? "inconnue" : "—", s.real_dwell != null && gap(s.plan_dwell, s.real_dwell))}
              <td className={`${cell} ${sep}`}><span className="inline-block w-28">{match}</span>
                {otherTruck(s.real_device_id) ? <span className="block text-[11px] text-slate-500">avec le {otherTruck(s.real_device_id)}</span> : null}</td>
            </tr>
            {(s.open_end || s.short || s.shared) && <tr><td colSpan={11} className="px-1.5 pb-1 text-[11.5px] font-semibold text-slate-600">
              {s.open_end ? "Aucun trajet après cet arrêt" : s.shared ? "Livré et chargé au même arrêt Geotab, partagé au prorata du prévu" : "Arrêt complet de moins de 5 min sur le site"} : durée sur place inconnue, exclue du Calibrage.</td></tr>}
            {s.lunch_after > 0 && (
              <tr><td className={cell} /><td className={cell} /><td className={`${cell} text-slate-600`}>Dîner prévu</td>
                {type("#fff", "Dîner")}{cells()}{cells(fmtDuration(s.lunch_after), "—")}<td className={`${cell} ${sep}`} /></tr>
            )}
            </React.Fragment>
          );
        })}
        {offBefore(cmp.real_return ?? Infinity).map(offRow)}
        <tr><td className={cell} /><td className={cell} /><td className={cell}><b className="text-slate-800">Retour au dépôt</b></td>{type(...KIND.depot)}{route(legs.at(-1))}{cells()}<td className={`${cell} ${sep}`} /></tr>
        {totals.map(([label, isRoute, pairs], i) => {
          const done = pairs.filter(([, real]) => real != null);
          const [plan, real] = [0, 1].map((k) => done.reduce((sum, pair) => sum + pair[k], 0));
          const top = i === 0 ? "border-t-2 border-t-slate-400" : "";
          const sum = done.length ? cells(fmtDuration(plan), <b className="text-slate-900">{fmtDuration(real)}</b>, gap(plan, real), top) : cells(null, null, null, top);
          return (
            <tr key={label}><td className={`${cell} ${top}`} /><td className={`${cell} ${top}`} />
              <td colSpan={2} className={`${cell} ${top} font-bold text-slate-900`}>{label} <span className="text-[11px] font-semibold text-slate-500">{done.length === pairs.length ? done.length : `${done.length} sur ${pairs.length}`}</span></td>
              {isRoute ? <>{sum}{cells(null, null, null, top)}</> : <>{cells(null, null, null, top)}{sum}</>}<td className={`${cell} ${sep} ${top}`} /></tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Le vrai Gantt du Dispatch pour ce camion, en lecture seule comme dans la Consolidation.
function DispatchGantt({ date, period, truckId }) {
  const [state, setState] = React.useState({});
  React.useEffect(() => {
    let cancelled = false;
    setState({});
    fetchGeotabAssignment(date, period, truckId)
      .then(({ assignment }) => { if (!cancelled) setState({ assignment }); })
      .catch((error) => { if (!cancelled) setState({ error }); });
    return () => { cancelled = true; };
  }, [date, period, truckId]);
  return (
    <section className="min-w-0 rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
      <div className="px-1 pb-1 text-[12px] font-bold text-slate-800">Gantt du Dispatch</div>
      {state.error ? <p role="alert" className="px-1 text-[12px] text-red-800">{state.error.message}</p>
        : !("assignment" in state) ? <p role="status" className="px-1 text-[12px] text-slate-500">Chargement du Gantt…</p>
        : <DispatchGanttPreview assignment={state.assignment} dispatchDate={date} dispatchPeriod={period} />}
    </section>
  );
}

function TruckDetail({ row, date, period, tolerance, findings, showGantt, onShowGantt, showReleve, onShowReleve }) {
  const [trace, setTrace] = React.useState([]);
  const segments = row.comparison ? JSON.stringify(traceSegments(row)) : "";
  React.useEffect(() => setTrace([]), [row.truck_id, date]);
  React.useEffect(() => {
    let cancelled = false;
    if (segments) {
      fetchGeotabTraces(date, JSON.parse(segments)).then((points) => { if (!cancelled) setTrace(points); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [segments, date]);

  const identity = (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-2">
        <div className="truncate text-[16px] font-bold leading-tight text-slate-900">{driverOf(row)}</div>
        {row.comparison && <span className="w-28 shrink-0"><GeotabBadge tone={row.comparison.order_ok ? "ok" : "warn"}>{row.comparison.order_ok ? "Ordre respecté" : "Ordre modifié"}</GeotabBadge></span>}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[12px] text-slate-500">
          {(row.trucks_used || []).some((u) => !u.planned)
            ? <>{row.trucks_used.length > 1 ? "Camions" : "Camion"} : {row.trucks_used.map((u, i) => <React.Fragment key={u.device_id}>{i ? ", puis " : ""}<b className="text-slate-700">{u.name}</b>{u.planned ? " (prévu)" : ""} {fmtTime(u.start)}–{fmtTime(u.end)}</React.Fragment>)}{(row.trucks_used || []).every((u) => !u.planned) ? <> · prévu : <b className="text-slate-700">{row.truck_name}</b></> : null}</>
            : <>Camion <b className="text-slate-700">{row.truck_name}</b>{row.device_name ? ` · boîtier ${row.device_name}` : ""}</>}
          {isOtherDriver(row) ? ` · session Geotab : ${row.driver_geotab}` : row.driver_check ? " · chauffeur confirmé au relevé" : ""}
        </div>
      </div>
    </div>
  );
  if (!row.plan || !row.comparison) {
    return (
      <div className="grid gap-2">
        <div className="rounded-[4px] border border-slate-200 bg-white px-3 py-1.5">{identity}</div>
        <p className="rounded-[4px] border border-slate-200 bg-white p-3 text-sm text-slate-600">{STATUS_TEXT[row.status]}</p>
      </div>
    );
  }
  const cmp = row.comparison;
  const plan = row.plan;
  const worked = cmp.end_at - cmp.real_departure - (plan.return_at - plan.departure);
  // Pas d'heure de départ ni de retour : un chauffeur peut partir à 5 h ou à 7 h. Les durées moyennes disent si le plan est juste.
  const stays = (kind) => cmp.sites.filter((s) => s.kind === kind).map((s) => [s.plan_dwell, s.real_dwell]);
  const averages = [["Route par trajet", driveLegs(row).map((l) => [l.plan, l.real]), "trajet"], ["Chargement", stays("loading"), "chargement"], ["Livraison", stays("delivery"), "livraison"]]
    .map(([label, pairs, unit]) => [label, meanDurations(pairs), unit]).filter(([, m]) => m);
  // Libellé, prévu, réel, écart, nombre d'étapes. Temps travaillé : du départ au retour au dépôt ; sans retour, jusqu'à la fin de la journée.
  const stats = [
    ["Temps travaillé", fmtDuration(plan.return_at - plan.departure), cmp.in_progress ? "—" : fmtDuration(cmp.end_at - cmp.real_departure),
      cmp.in_progress ? <GeotabBadge tone="mute">En cours</GeotabBadge> : <GeotabBadge tone={deltaTone(worked, tolerance)}>{fmtDelta(worked)}</GeotabBadge>],
    ...averages.map(([label, m, unit]) => [label, fmtDuration(m.plan), fmtDuration(m.real), <GeotabBadge tone={deltaTone(m.gap, tolerance)}>{fmtDelta(m.gap)}</GeotabBadge>,
      m.count === m.of ? `${m.count} ${unit}${m.count > 1 ? "s" : ""}` : `${m.count} sur ${m.of}`]),
  ];
  const plannedLine = plan.geometry?.length ? plan.geometry : [plan.depot, ...plan.sites.map((s) => [s.lat, s.lon]), plan.depot];
  const layers = {
    depot: plan.depot,
    plannedLine,
    realLine: trace.length ? trace : cmp.visits.filter((v) => !v.after_day).map((v) => [v.lat, v.lon]),
    sites: cmp.sites.map((s) => ({ ...s, label: s.kind === "depot" ? "D" : s.idx, missing: s.match === "missing", title: `${s.idx}. ${s.name} — prévu ${fmtTime(s.plan_arrival)}` })),
    realStops: cmp.visits.filter((v) => !v.after_day && (v.site_idx || ((v.dwell >= 10 || v.off) && v.kind !== "depot"))).map((v) => ({
      lat: v.lat, lon: v.lon, off: !v.site_idx && v.kind !== "loading", title: `${v.site_idx ? `${v.site_idx}` : v.kind === "loading" ? `Chargement ${v.name}` : "Hors plan"} — réel ${fmtTime(v.arr)} → ${fmtTime(v.dep)}`,
    })),
    offsetLinks: cmp.sites.filter((s) => s.real_lat != null && s.offset_m > 300).map((s) => [[s.lat, s.lon], [s.real_lat, s.real_lon], `Écart d'adresse : ${fmtDistance(s.offset_m)}`]),
  };
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-[4px] border border-slate-200 bg-white px-3 py-1.5">
        <div className="min-w-[240px] flex-1">{identity}</div>
        {/* Sur téléphone, le tableau défile dans son cadre au lieu d'élargir la page. */}
        <div className="max-w-full overflow-x-auto"><StatTable stats={stats} /></div>
        <div className="flex flex-wrap gap-1.5 self-start">
          {onShowReleve ? <button type="button" className={showReleve ? LINK_PRESSED : LINK} aria-pressed={showReleve} onClick={() => onShowReleve(!showReleve)}>Relevé de Consolidation</button> : null}
          <button type="button" className={showGantt ? LINK_PRESSED : LINK} aria-pressed={showGantt} onClick={() => onShowGantt(!showGantt)}>Gantt du Dispatch</button>
        </div>
        {cmp.return_delta != null && <div className="basis-full"><WhyLine cmp={cmp} worked={worked} tolerance={tolerance} /></div>}
      </div>
      {showGantt && <DispatchGantt date={date} period={period} truckId={row.truck_id} />}
      <section className="rounded-[4px] border border-slate-200 bg-white px-2 py-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-[11px] text-slate-500">
          <span className="text-[12px] font-bold text-slate-800">Gantt prévu / réel</span>
          <span>Bloc à contour bleu pointillé : adresse à vérifier · Hachuré orange : arrêt hors plan · Lignes : écart à l'arrivée</span>
        </div>
        <GeotabGantt row={row} tolerance={tolerance} />
      </section>
      {/* Durées par étape | constats, puis carte | journée du chauffeur jusqu'au bas de l'écran. Le relevé prend la place du tableau et de la carte. */}
      <div className={`grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_340px] gap-2 @max-[1650px]:grid-cols-1 @max-[1650px]:grid-rows-none ${showReleve ? "grid-rows-[auto_1fr]" : "grid-rows-[auto_minmax(280px,1fr)]"}`}>
        <section className={`min-w-0 overflow-x-auto rounded-[4px] border border-slate-200 bg-white px-2 py-1.5 ${showReleve ? "row-span-2 @max-[1650px]:row-span-1" : ""}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-1 text-[11px] text-slate-500">
            <span className="text-[12px] font-bold text-slate-800">{showReleve ? "Relevé de Consolidation" : "Durées par étape"}</span>
            <span className="flex items-center gap-2">
              {!showReleve && <span>Route = trajet depuis l'étape précédente, sans les arrêts hors plan ni le dîner prévu · Sur place = arrêt dans le rayon jusqu'à la reprise de la route</span>}
              {onShowReleve ? <span className="inline-flex shrink-0 overflow-hidden whitespace-nowrap rounded-[4px] border border-slate-300" role="group" aria-label="Affichage sous le Gantt">
                {[[false, "Heures Geotab"], [true, "Relevé"]].map(([value, label]) => (
                  <button key={label} type="button" aria-pressed={showReleve === value} onClick={() => onShowReleve(value)}
                    className={`h-6 px-2 text-[12px] font-semibold ${showReleve === value ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
                ))}
              </span> : null}
            </span>
          </div>
          {showReleve ? <GeotabReleve date={date} period={period} truckId={row.truck_id} /> : <SiteTable row={row} tolerance={tolerance} />}
        </section>
        {findings}
        {!showReleve && <GeotabMap layers={layers} fitKey={`${row.truck_id}-${date}-${trace.length > 0}`} className="h-full min-h-[280px]" />}
        <GeotabDriverDay row={row} />
      </div>
    </div>
  );
}

export default function GeotabComparisonView({ date, period = "day", thresholds, initialTruck = null, onDateChange, onPeriodChange, preferences, onPreferencesChange, onTruckChange }) {
  // Le relevé vient de la Consolidation : sans « Consulter la consolidation », ni bouton ni bascule « Relevé ».
  const canReleve = useAccess().hasPermission("billing.consolidation.read");
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [selected, setSelectedState] = React.useState(initialTruck);
  const setSelected = (id) => { setSelectedState(id); onTruckChange?.(id); };
  const [refresh, setRefresh] = React.useState(0);
  const [loadedAt, setLoadedAt] = React.useState(0);
  const loadedKey = React.useRef("");
  const requestedAt = React.useRef(0);
  const live = date === getLocalTodayDispatchDate();
  const planMode = preferences.comparison_plan === "closure" ? "closure" : "current";

  React.useEffect(() => {
    if (!live) return undefined;
    const due = () => document.visibilityState === "visible" && Date.now() - requestedAt.current >= REFRESH_MS && setRefresh((n) => n + 1);
    const timer = setInterval(due, 15000);
    document.addEventListener("visibilitychange", due);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", due); };
  }, [live]);

  React.useEffect(() => {
    let cancelled = false;
    const key = `${date}|${period}|${planMode}|${thresholds.radius_m}|${thresholds.tolerance_min}|${thresholds.min_off_min}`;
    // Une actualisation garde l'affichage en place ; seul un changement de journée ou de seuil montre le chargement.
    const silent = loadedKey.current === key;
    requestedAt.current = Date.now();
    if (!silent) {
      setBusy(true);
      setError(null);
    }
    fetchGeotabComparison(date, period, thresholds, planMode)
      .then((payload) => {
        if (cancelled) return;
        loadedKey.current = key;
        setData(payload);
        setLoadedAt(Date.now());
        setSelectedState((current) => (current === FLEET || payload.trucks.some((t) => t.truck_id === current) ? current : payload.trucks.find((t) => t.comparison)?.truck_id || payload.trucks[0]?.truck_id || null));
      })
      .catch((err) => { if (!cancelled && !silent) setError(err); })
      .finally(() => { if (!cancelled && !silent) setBusy(false); });
    return () => { cancelled = true; };
  }, [date, period, thresholds, refresh, planMode]);

  // Un changement de date ou de période ne doit jamais montrer les indicateurs de la sélection précédente.
  const matchesDay = data?.date === date && data?.period === period;
  const rows = [...(matchesDay ? data.trucks : [])].sort((a, b) => (!a.comparison - !b.comparison) || driverOf(a).localeCompare(driverOf(b), "fr"));
  const row = rows.find((t) => t.truck_id === selected);
  // Journée en cours : un site dont l'heure prévue n'est pas encore passée n'est pas « non vu ».
  const now = live && loadedAt ? new Date(loadedAt).getHours() * 60 + new Date(loadedAt).getMinutes() : Infinity;
  const findingOptions = { tolerance: thresholds.tolerance_min, now };
  // Rayon d'arrivée, tolérance, arrêt hors plan et couleurs : à droite de la ligne « Horaires » de la barre, pour gagner une ligne en 1080p.
  const settings = (
    <>
      {[["radius_m", "Rayon d'arrivée"], ["tolerance_min", "Tolérance"], ["min_off_min", "Arrêt hors plan ≥"]].map(([key, label]) => (
        <label key={key} className="inline-flex items-center gap-1">{label}
          <select className={SELECT} value={thresholds[key]} onChange={(e) => onPreferencesChange({ [key]: Number(e.target.value) })}>
            {THRESHOLD_CHOICES[key].map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
        </label>
      ))}
      {/* Même légende que le Calibrage : la couleur dit le sens, la teinte foncée plus du double de la tolérance. */}
      <HoverRows title="Couleurs des écarts" rows={[["Sur la cible", `± ${thresholds.tolerance_min} min`],
        ["Plus tard ou plus long", `jaune jusqu'à ${2 * thresholds.tolerance_min} min, rouge au-delà`],
        ["Plus tôt ou plus court", `violet jusqu'à ${2 * thresholds.tolerance_min} min, violet foncé au-delà`]]}>
        <span className="cursor-help underline decoration-dotted underline-offset-2">Couleurs</span>
      </HoverRows>
    </>
  );
  return (
    <div className="@container flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50" onClick={() => onDateChange(shiftDate(date, -1))} aria-label="Jour précédent"><ChevronLeft size={15} /></button>
        <GeotabDayPicker value={date} onChange={onDateChange} label="Journée de dispatch" />
        <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50" onClick={() => onDateChange(shiftDate(date, 1))} aria-label="Jour suivant"><ChevronRight size={15} /></button>
        <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Période du Dispatch">
          {[["day", "Jour"], ["evening", "Soir"]].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={period === key} onClick={() => onPeriodChange(key)}
              className={`h-7 px-3 text-[12.5px] font-semibold ${period === key ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </span>
        <span className="inline-flex flex-wrap items-center gap-1.5 text-[12px] text-slate-500">
          Prévu : <b className="text-slate-700">{data?.plan_source === "closure" ? `Dispatch clôturé${data.plan_closed_at ? ` le ${fmtStamp(data.plan_closed_at)}` : ""}` : data?.plan_source === "latest" ? "Dispatch en cours" : "—"}</b>
          {data?.plan_source === "latest" && <span className="w-36"><GeotabBadge tone="warn" title="Le plan de référence est le Dispatch en cours, réaffectations manuelles comprises. À la clôture, cette version est conservée. En « Réglages actuels », ses durées restent recalculées.">Journée non clôturée</GeotabBadge></span>}
          {/* Le plan tel qu'il a été clôturé, ou rejoué avec les réglages actuels : les mêmes durées que le Calibrage. */}
          <span className="inline-flex overflow-hidden rounded-[4px] border border-slate-300" role="group" aria-label="Heures prévues">
            {[["closure", "Plan de clôture"], ["current", "Réglages actuels"]].map(([value, label]) => (
              <HoverCard key={value} title="Quel prévu comparer au réel ?" content={PLAN_COMPARISON_HELP} placement="bottom" delay={120}
                referenceAsChild flat compactTitle interactive openOnTouch maxWidth={480} accentColor="var(--color-accent)">
                <button type="button" aria-pressed={planMode === value} onClick={() => onPreferencesChange({ comparison_plan: value })}
                  className={`h-6 px-2 text-[12px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent ${planMode === value ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{label}</button>
              </HoverCard>
            ))}
          </span>
          {planMode === "current" && data?.plan_settings?.length > 0 && (
            <Hint text="Le plan garde ses tournées et son ordre ; seules les durées sont recalculées.">
              <span className="text-[11px] text-slate-500">rejoué avec : {data.plan_settings.map((p) => `${p.label.toLowerCase()} ${p.closure ?? "—"} → ${p.current ?? "—"} ${p.unit}`).join(" · ")}</span>
            </Hint>
          )}
          · Réel : <Hint text={live ? "Les trajets de la journée en cours sont relus toutes les 2 minutes, seulement quand cet onglet est affiché." : ""}>
            <b className="text-slate-700">{live && loadedAt > 0 ? `trajets Geotab jusqu'à ${fmtTime(new Date(loadedAt).getHours() * 60 + new Date(loadedAt).getMinutes())}` : "trajets Geotab de la journée"}</b>
          </Hint>
        </span>
        {/* Sans la barre (aucun camion à comparer), les réglages restent ici ; sinon, à droite de sa ligne « Horaires ». */}
        {!(data && !busy && rows.length > 0) && <span className="ml-auto flex flex-wrap items-center gap-2 text-[12px] text-slate-600">{settings}</span>}
      </div>
      {busy && <GeotabBusy title="Chargement des trajets Geotab…" detail="Comparaison du plan et des trajets de la journée" />}
      {error && <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error.message}</p>}
      {matchesDay && !busy && (
        <>
          {data.plan_source === "none" && <p className="rounded-[4px] border border-slate-200 bg-white p-3 text-sm text-slate-600">{period === "evening" ? "Aucun Dispatch du soir pour cette journée." : "Aucun Dispatch pour cette journée."}</p>}
          {rows.length > 0 && (
            <>
              <GeotabPlanBar bar={data.bar} timing={data.bar?.timing} scope="day" date={data.date} period={data.period} tolerance={thresholds.tolerance_min} aside={settings} />
              {/* À gauche la flotte (liste, constats de la journée), à droite le camion choisi jusqu'au bas de l'écran. */}
              <div className="grid flex-1 grid-cols-[auto_minmax(0,1fr)] gap-2 @max-[1040px]:grid-cols-1">
                <div className="grid min-w-0 content-start gap-2">
                  <TruckList rows={rows} selected={selected} onSelect={setSelected} tolerance={thresholds.tolerance_min} />
                  {/* w-0 min-w-full : la largeur de la colonne reste celle de la liste. */}
                  <div className="w-0 min-w-full"><DayFindings items={data.summary.day_items || []} tolerance={thresholds.tolerance_min} /></div>
                </div>
                {selected === FLEET && (
                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_340px] items-start gap-2 @max-[1650px]:grid-cols-1">
                    <GeotabFleetSummary rows={rows} durations={data.bar?.timing?.durations} tolerance={thresholds.tolerance_min} onSelect={setSelected} />
                    <FindingsPanel title="Constats · Toute la flotte" items={fleetItems(rows, findingOptions)} tolerance={thresholds.tolerance_min} />
                  </div>
                )}
                {row && <TruckDetail row={row} date={date} period={period} tolerance={thresholds.tolerance_min}
                  findings={row.comparison && <FindingsPanel title={`Constats · ${driverOf(row)}`} items={truckItems(row, findingOptions)} tolerance={thresholds.tolerance_min} />}
                  showGantt={preferences.dispatch_gantt} onShowGantt={(value) => onPreferencesChange({ dispatch_gantt: value })}
                  showReleve={canReleve && preferences.releve} onShowReleve={canReleve ? (value) => onPreferencesChange({ releve: value }) : null} />}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
